import { reactive, readonly } from 'vue';
import { user, balance, profile } from '@/api';
import { PrivateRewardBalancePayload } from '@/types/interfaces/balance';
import { IUserPayload } from '@/types/interfaces/user';

interface State {
	privateReward: {
		balance?: number;
		available_balance: number;
		cashback_amount: number;
		cashback_used_balance: number;
		max_bonus_usage_percentage: number;
	};
}

const state = reactive<State>({
	privateReward: {
		balance: undefined,
		available_balance: 0,
		cashback_amount: 0,
		cashback_used_balance: 0,
		max_bonus_usage_percentage: 0
	}
});

/**
 * Получение данных о пользователе
 */
const fetchUser = () => user.get();

/**
 * Получение профиля пользователя (содержит последний выбранный язык)
 */
const fetchProfile = () => profile.getUserProfile();

/**
 * Получение баланса пользователя
 */
const fetchChocoBalance = (): Promise<number> => {
	return balance
		.getUserBalance()
		.then(response => response.data.bonus.KZT + response.data.money.KZT)
		.catch(() => 0);
};

const fetchPrivateReward = (payload: PrivateRewardBalancePayload) => {
	return balance.getPrivateRewardBalance(payload);
};

const fetchBalance = async (
	payload?: PrivateRewardBalancePayload,
	force = false
) => {
	if (state.privateReward.balance && !force) {
		return state.privateReward;
	}

	const { terminal_id, is_chocobalance_hidden } = payload || {};

	if (!terminal_id) {
		if (!is_chocobalance_hidden) {
			state.privateReward.balance = await fetchChocoBalance();
		}

		return state.privateReward;
	}

	try {
		const data = await fetchPrivateReward(payload || {});
		state.privateReward = data.attributes;
	} catch {
		if (!is_chocobalance_hidden) {
			state.privateReward.balance = await fetchChocoBalance();
		}
	}

	return state.privateReward;
};

const clearPrivateReward = () => {
	state.privateReward = {
		balance: undefined,
		available_balance: 0,
		cashback_amount: 0,
		cashback_used_balance: 0,
		max_bonus_usage_percentage: 0
	};
};

/**
 * Обновление языка в профиле пользователя
 */
const updateProfileLanguage = (language: string) => {
	return profile.setProfileLanguage(language);
};

/**
 * Обновление данных о пользователе
 */
const setUserProfile = (payload: IUserPayload) => user.updateUser(payload);

const UserService = {
	state: readonly(state),
	fetchUser,
	fetchProfile,
	fetchBalance,
	clearPrivateReward,
	updateProfileLanguage,
	setUserProfile,
	deleteAvatar: user.deleteAvatar,
	uploadAvatar: user.postAvatar
};

export { UserService };
